import { computed, DestroyRef, inject, Injectable, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CooperationGroup } from '@app/shared/types/models/cooperation/cooperation-group.model';
import { CooperationProject } from '@app/shared/types/models/cooperation/cooperation-project.model';
import { CooperationUser } from '@app/shared/types/models/cooperation/cooperation-user.model';
import { Cooperation } from '@app/shared/types/models/cooperation/cooperation.model';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { AppEvent } from '../types/models/notifications/app-event';
import { CompaniesDataService } from './companies-data.service';
import { DataService } from './data-service';
import { CooperationGroupDTO } from './dtos/cooperation/cooperation-group.dto';
import { CooperationProjectDTO } from './dtos/cooperation/cooperation-project.dto';
import { CooperationStatusEnum } from './dtos/cooperation/cooperation-status.enum';
import { CooperationUserDTO } from './dtos/cooperation/cooperation-user.dto';
import { CooperationDTO } from './dtos/cooperation/cooperation.dto';
import { CooperationGroupFactory } from './factories/cooperation-group.factory';
import { CooperationProjectFactory } from './factories/cooperation-project.factory';
import { CooperationUserFactory } from './factories/cooperation-user.factory';
import { CooperationFactory } from './factories/cooperation.factory';
import { GroupsDataService } from './groups-data.service';
import { CooperationsHttpService } from './http/cooperations-http.service';
import { ProjectsDataService } from './projects-data.service';
import { UsersDataService } from './users-data.service';

@Injectable({ providedIn: 'root' })
export class CooperationsDataService extends DataService<CooperationDTO, Cooperation> {
  private readonly http = inject(CooperationsHttpService);
  private readonly factory = inject(CooperationFactory);
  private readonly cooperationGroupFactory = inject(CooperationGroupFactory);
  private readonly cooperationProjectFactory = inject(CooperationProjectFactory);
  private readonly cooperationUserFactory = inject(CooperationUserFactory);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly companiesDataService = inject(CompaniesDataService);
  private readonly groupsDataService = inject(GroupsDataService);
  private readonly projectsDataService = inject(ProjectsDataService);
  private readonly usersDataService = inject(UsersDataService);

  private readonly cooperations: DataStore<Cooperation> = new DataStore<Cooperation>();
  private readonly incomingByCompany: DataStore<IdsCollection> = new DataStore<IdsCollection>();
  private readonly outgoingByCompany: DataStore<IdsCollection> = new DataStore<IdsCollection>();
  private readonly cooperationGroups: DataStore<CooperationGroup> = new DataStore<CooperationGroup>();
  private readonly cooperationGroupsByCooperation: DataStore<IdsCollection> = new DataStore<IdsCollection>();
  private readonly cooperationProjects: DataStore<CooperationProject> = new DataStore<CooperationProject>();
  private readonly cooperationProjectsByCooperation: DataStore<IdsCollection> = new DataStore<IdsCollection>();
  private readonly cooperationUsers: DataStore<CooperationUser> = new DataStore<CooperationUser>();
  private readonly cooperationUsersByCooperation: DataStore<IdsCollection> = new DataStore<IdsCollection>();

  /** Returns the i18n translation key for the given cooperation status (e.g. 'cooperations.status-pending'). */
  public getStatusLabelKey(status: number | undefined): string {
    switch (status) {
      case CooperationStatusEnum.Pending:
        return 'cooperations.status-pending';
      case CooperationStatusEnum.Accepted:
        return 'cooperations.status-accepted';
      case CooperationStatusEnum.Rejected:
        return 'cooperations.status-rejected';
      case CooperationStatusEnum.Deactivated:
        return 'cooperations.status-deactivated';
      default:
        return 'cooperations.status-unknown';
    }
  }

  public getById(cooperationId: string): Signal<Cooperation | undefined> {
    return this.cooperations.get(cooperationId);
  }

  public getIncoming(companyId: string): Signal<Cooperation[] | undefined> {
    return computed(
      () =>
        this.incomingByCompany
          .get(companyId)()
          ?.ids?.map(id => this.cooperations.get(id)()) || undefined
    );
  }

  public getOutgoing(companyId: string): Signal<Cooperation[] | undefined> {
    return computed(
      () =>
        this.outgoingByCompany
          .get(companyId)()
          ?.ids?.map(id => this.cooperations.get(id)()) || undefined
    );
  }

  public getCooperationGroups(cooperationId: string): Signal<CooperationGroup[] | undefined> {
    return computed(
      () =>
        this.cooperationGroupsByCooperation
          .get(cooperationId)()
          ?.ids?.map(id => this.cooperationGroups.get(id)()) || undefined
    );
  }

  public getCooperationProjects(cooperationId: string): Signal<CooperationProject[] | undefined> {
    return computed(
      () =>
        this.cooperationProjectsByCooperation
          .get(cooperationId)()
          ?.ids?.map(id => this.cooperationProjects.get(id)()) || undefined
    );
  }

  public getCooperationUsers(cooperationId: string): Signal<CooperationUser[] | undefined> {
    return computed(
      () =>
        this.cooperationUsersByCooperation
          .get(cooperationId)()
          ?.ids?.map(id => this.cooperationUsers.get(id)()) || undefined
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected handleEvent(event: AppEvent): void {}

  public loadById(cooperationId: string): void {
    if (!cooperationId || this.cooperations.hasDataForId(cooperationId)) return;

    this.cooperations.reserveId(cooperationId);

    this.http
      .getById(cooperationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: dto => {
          if (dto.recipient) {
            this.companiesDataService.upsertLocalData(dto.recipient);
          }
          if (dto.requestor) {
            this.companiesDataService.upsertLocalData(dto.requestor);
          }
          this.cooperations.upsert(this.factory.produce(dto));
        }
      });
  }

  public loadIncoming(companyId: string): void {
    if (!companyId || this.incomingByCompany.hasDataForId(companyId)) return;

    this.http
      .getIncoming(companyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: dtos => {
          if (!dtos.length) {
            this.incomingByCompany.upsert({ id: companyId, ids: [] });
            return;
          }

          dtos.forEach(dto => {
            if (dto.recipient) {
              this.companiesDataService.upsertLocalData(dto.recipient);
            }
            if (dto.requestor) {
              this.companiesDataService.upsertLocalData(dto.requestor);
            }
            this.cooperations.upsert(this.factory.produce(dto));

            if (dto.cooperationGroups?.length) {
              this.processCooperationGroups(dto.id, dto.cooperationGroups);
            }
            if (dto.coopreationProjects?.length) {
              this.processCooperationProjects(dto.id, dto.coopreationProjects);
            }
          });
          this.incomingByCompany.upsert({ id: companyId, ids: dtos.map(dto => dto.id) });
        }
      });
  }

  public loadOutgoing(companyId: string): void {
    if (!companyId || this.outgoingByCompany.hasDataForId(companyId)) return;

    this.http
      .getOutgoing(companyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: dtos => {
          if (!dtos.length) {
            this.outgoingByCompany.upsert({ id: companyId, ids: [] });
            return;
          }

          dtos.forEach(dto => {
            if (dto.recipient) {
              this.companiesDataService.upsertLocalData(dto.recipient);
            }
            if (dto.requestor) {
              this.companiesDataService.upsertLocalData(dto.requestor);
            }
            this.cooperations.upsert(this.factory.produce(dto));

            if (dto.cooperationGroups?.length) {
              this.processCooperationGroups(dto.id, dto.cooperationGroups);
            }
            if (dto.coopreationProjects?.length) {
              this.processCooperationProjects(dto.id, dto.coopreationProjects);
            }
          });
          this.outgoingByCompany.upsert({ id: companyId, ids: dtos.map(dto => dto.id) });
        }
      });
  }

  public loadCooperationGroups(cooperationId: string): void {
    if (!cooperationId || this.cooperationGroupsByCooperation.hasDataForId(cooperationId)) return;

    this.http
      .getCooperationGroups(cooperationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: dtos => {
          this.processCooperationGroups(cooperationId, dtos);
        }
      });
  }

  protected processCooperationGroups(cooperationId: string, dtos: CooperationGroupDTO[]): void {
    if (!dtos.length) {
      this.cooperationGroupsByCooperation.upsert({ id: cooperationId, ids: [] });
      return;
    }
    dtos.forEach(dto => {
      const model = this.cooperationGroupFactory.produce(dto);
      this.cooperationGroups.upsert(model);
    });
    this.cooperationGroupsByCooperation.upsert({
      id: cooperationId,
      ids: dtos.map(dto => dto.id)
    });
  }

  public loadCooperationProjects(cooperationId: string): void {
    if (!cooperationId || this.cooperationProjectsByCooperation.hasDataForId(cooperationId)) return;

    this.http
      .getCooperationProjects(cooperationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: dtos => {
          this.processCooperationProjects(cooperationId, dtos);
        }
      });
  }

  protected processCooperationProjects(cooperationId: string, dtos: CooperationProjectDTO[]): void {
    if (!dtos.length) {
      this.cooperationProjectsByCooperation.upsert({ id: cooperationId, ids: [] });
      return;
    }
    dtos.forEach(dto => {
      const model = this.cooperationProjectFactory.produce(dto);
      this.cooperationProjects.upsert(model);
    });
    this.cooperationProjectsByCooperation.upsert({
      id: cooperationId,
      ids: dtos.map(dto => dto.id)
    });
  }

  /** Updates local cache with cooperation projects (e.g. after POST). */

  public upsertLocalData(dto: CooperationDTO): Signal<Cooperation> {
    if (!dto) return undefined;

    if (dto.recipient) {
      this.companiesDataService.upsertLocalData(dto.recipient);
    }
    if (dto.requestor) {
      this.companiesDataService.upsertLocalData(dto.requestor);
    }

    const result = this.factory.produce(dto);
    return this.cooperations.upsert(result);
  }

  public upsertCooperationProjects(
    cooperationId: string,
    dtos: CooperationProjectDTO[]
  ): Signal<CooperationProject[]> | undefined {
    if (!cooperationId) return;
    if (!dtos.length) {
      this.cooperationProjectsByCooperation.upsert({ id: cooperationId, ids: [] });
      return signal([]);
    }
    dtos.forEach(dto => {
      if (dto.project) {
        this.projectsDataService.upsertLocalData(dto.project);
      }

      const model = this.cooperationProjectFactory.produce(dto);
      this.cooperationProjects.upsert(model);
    });
    this.cooperationProjectsByCooperation.upsert({
      id: cooperationId,
      ids: dtos.map(dto => dto.id)
    });
    return this.getCooperationProjects(cooperationId);
  }

  /** Updates local cache with cooperation groups (e.g. after POST). */
  public upsertCooperationGroups(
    cooperationId: string,
    dtos: CooperationGroupDTO[]
  ): Signal<CooperationGroup[]> | undefined {
    if (!cooperationId) return;
    if (!dtos.length) {
      this.cooperationGroupsByCooperation.upsert({ id: cooperationId, ids: [] });
      return signal([]);
    }
    dtos.forEach(dto => {
      if (dto.group) {
        this.groupsDataService.upsertOnlyNewLocalData(dto.group);
      }
      this.cooperationGroups.upsert(this.cooperationGroupFactory.produce(dto));
    });
    this.cooperationGroupsByCooperation.upsert({
      id: cooperationId,
      ids: dtos.map(dto => dto.id)
    });
    return this.getCooperationGroups(cooperationId);
  }

  public loadCooperationUsers(cooperationId: string): void {
    if (!cooperationId || this.cooperationUsersByCooperation.hasDataForId(cooperationId)) return;

    this.http
      .getCooperationUsers(cooperationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: dtos => {
          if (!dtos.length) {
            this.cooperationUsersByCooperation.upsert({ id: cooperationId, ids: [] });
            return;
          }
          dtos.forEach((dto: CooperationUserDTO) => {
            if (dto.user) {
              this.usersDataService.upsertLocalData(dto.user);
            }
            this.cooperationUsers.upsert(this.cooperationUserFactory.produce(dto));
          });
          this.cooperationUsersByCooperation.upsert({
            id: cooperationId,
            ids: dtos.map(dto => dto.id)
          });
        }
      });
  }

  /** Updates local cache with a single cooperation user (e.g. after POST). */
  public upsertCooperationUser(dto: CooperationUserDTO): Signal<CooperationUser> | undefined {
    if (!dto?.id) return;

    if (dto.user) {
      this.usersDataService.upsertLocalData(dto.user);
    }

    const result = this.cooperationUsers.upsert(this.cooperationUserFactory.produce(dto));

    const existing = this.cooperationUsersByCooperation.get(dto.cooperationId)();
    const ids = existing?.ids ?? [];
    if (!ids.includes(dto.id)) {
      this.cooperationUsersByCooperation.upsert({
        id: dto.cooperationId,
        ids: [...ids, dto.id]
      });
    }

    return result;
  }

  /** Removes a cooperation user from local cache (e.g. after DELETE). */
  public removeCooperationUser(cooperationId: string, cooperationUserId: string): void {
    if (!cooperationId || !cooperationUserId) return;
    this.cooperationUsers.delete(cooperationUserId);
    const existing = this.cooperationUsersByCooperation.get(cooperationId)();
    const ids = existing?.ids?.filter(id => id !== cooperationUserId) ?? [];
    this.cooperationUsersByCooperation.upsert({ id: cooperationId, ids });
  }

  public upsertOutgoing(companyId: string, cooperationDTO: CooperationDTO): Cooperation | undefined {
    if (!companyId || !cooperationDTO) return undefined;

    if (cooperationDTO.recipient) {
      this.companiesDataService.upsertLocalData(cooperationDTO.recipient);
    }
    if (cooperationDTO.requestor) {
      this.companiesDataService.upsertLocalData(cooperationDTO.requestor);
    }

    const cooperation = this.factory.produce(cooperationDTO);
    this.cooperations.upsert(cooperation);

    const existing = this.outgoingByCompany.hasDataForId(companyId)
      ? this.outgoingByCompany.get(companyId)()
      : undefined;

    const existingIds = existing?.ids ?? [];
    if (existingIds.includes(cooperation.id)) {
      return;
    }

    this.outgoingByCompany.upsert({
      id: companyId,
      ids: [...existingIds, cooperation.id]
    });

    this.notificationService.notify({
      eventType: 'CooperationCreated',
      data: cooperation
    });

    return cooperation;
  }
}
